import React, {Component, ErrorInfo, ReactNode} from 'react'
import {getLogger} from '../services/logging'
import {getRootState} from '../store/store'
import Alert from './common/Alert'
import {getActionHistory} from '../store/middleware/actionHistoryMiddleware'
import {pushDataLayerEvent} from '../services/dataLayer'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

const logger = getLogger('AppErrorBoundary')

export default class AppErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  public state: ErrorBoundaryState = {
    hasError: false
  }

  public static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return {hasError: true}
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const {
      appView,
      booking,
      config,
      guestInfo,
      searchParams,
      searchSubmitted,
      shoppingCart,
      shoppingCartValidation,
      toastNotification,
      user
    } = getRootState()

    // log the error
    logger.error({
      message: `Application Error: ${error.message}`,
      errorData: {
        error: error.message,
        stack: error.stack,
        errorInfo
      }
    })

    // log the redux state
    logger.error({
      message: 'Application Error: Redux State',
      data: {
        appView,
        booking,
        guestInfo,
        searchParams,
        searchSubmitted,
        shoppingCart,
        shoppingCartValidation,
        toastNotification,
        user
      }
    })

    // log the action history
    logger.error({
      message: 'Application Error: Redux Action History',
      data: {
        actionHistory: getActionHistory()
      }
    })

    // log the app config
    logger.error({
      message: 'Application Error: Config',
      data: {
        config
      }
    })

    pushDataLayerEvent({
      event: `App_Error`,
      category: 'IBE',
      value: 1
    })
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="mx-auto px-4 mt-4 lg:mt-8 max-w-screen-xl">
          <Alert type="error" className="p-8">
            An application error occurred. If the problem persists, please
            contact support:{' '}
            <a
              className="link ml-1"
              target="_blank"
              href="mailto:hello@placemakr.com"
              aria-label="Email the Placemakr support team."
            >
              hello@placemakr.com
            </a>
          </Alert>
        </div>
      )
    } else {
      return <>{this.props.children}</>
    }
  }
}
